import { Link, useLocation, useNavigate } from "react-router";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Pencil, QrCode, Trash2 } from "lucide-react";
import { USER_STATUS_LABELS, createCounterSchema, type Paginated, type UserDto } from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Modal,
  Page,
  PageHeader,
  Pagination,
  RowActions,
  SearchInput,
  SelectField,
  SlideOver,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "@/components/ListFilters";
import { TempPasswordModal } from "@/components/TempPasswordModal";
import { QrPosterPanel } from "@/components/QrPosterPanel";
import { clearFieldError, PhoneField, validateFieldOnBlur, validateForm } from "@/components/FormField";
import { useTenant } from "@/tenant/TenantProvider";
import { useLanguage } from "@/i18n/language";
import { useCan } from "@/auth/access";
import { useOrgRoles } from "@/auth/useOrgRoles";
import {
  AddressFields,
  EMPTY_ADDRESS,
  FormSection,
  type AddressFormState,
} from "@/components/AddressFields";

/** `registrationUrl` is what the counter's QR opens (D-22); null while no WhatsApp is linked. */
type Counter = UserDto & { karigarCount: number; registrationUrl?: string | null };

const PAGE_SIZE = 20;

/** A counter records the same place a depot does — see `AddressFields`. */
interface CreateForm extends AddressFormState {
  name: string;
  email: string;
  phone: string;
  salesRepId: string;
  supplierType: "org" | "depot" | "distributor";
  supplierId: string;
}

const EMPTY_FORM: CreateForm = {
  ...EMPTY_ADDRESS,
  name: "",
  email: "",
  phone: "",
  salesRepId: "",
  supplierType: "org",
  supplierId: "",
};

interface SuppliersData {
  depots: Array<{ id: string; name: string; code: string }>;
  distributors: Array<{ id: string; name: string }>;
}

export function CountersPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { payload } = useTenant();
  const can = useCan();
  const canCreate = can("counters", "create");
  const canEdit = can("counters", "edit");
  const canDelete = can("counters", "delete");
  const seeTeam = Boolean(can("team"));
  // Within their own chain a new counter hangs off its creator, and an edit
  // cannot move it — so the placement fields are the org-wide editor's alone.
  const placeOnCreate = canCreate === "org";
  const placeOnEdit = canEdit === "org";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<Counter> | null>(null);
  const [loading, setLoading] = useState(true);
  const [salesReps, setSalesReps] = useState<UserDto[]>([]);
  const orgRoles = useOrgRoles();
  /*
   * An org whose chain has no sales rep tier was still being shown a "Sales
   * rep" select reading "None" — a field for a role that does not exist here,
   * which reads as a gap to fill rather than a tier that was never enabled.
   * While the roles are still loading, fall back to whether any rep exists.
   */
  const hasSalesRepTier = orgRoles
    ? orgRoles.enabledTiers.includes("sales_rep")
    : salesReps.length > 0;
  const [suppliers, setSuppliers] = useState<SuppliersData>({ depots: [], distributors: [] });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<{ name: string; signInWith: string; tempPassword: string } | null>(
    null,
  );

  const [selected, setSelected] = useState<Counter | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editSalesRepId, setEditSalesRepId] = useState("");
  const [editSupplierType, setEditSupplierType] = useState<"org" | "depot" | "distributor">("org");
  const [editSupplierId, setEditSupplierId] = useState("");
  const [editAddress, setEditAddress] = useState<AddressFormState>(EMPTY_ADDRESS);
  const [savingEdit, setSavingEdit] = useState(false);
  const [toDelete, setToDelete] = useState<Counter | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [qrCounter, setQrCounter] = useState<Counter | null>(null);

  const lookups = seeTeam || Boolean(canCreate || canEdit);
  useEffect(() => {
    if (!lookups) return;
    if (seeTeam) {
      api
        .get<Paginated<UserDto>>("/org/sales-reps?page=1&pageSize=100")
        .then((res) => setSalesReps(res.items))
        .catch(() => undefined);
    }

    api
      .get<{
        depots?: Array<{ id: string; name: string; code: string }>;
        distributors?: Array<{ id: string; name: string }>;
        suppliers?: Array<{ id: string; name: string; type: "org" | "depot" | "distributor"; code?: string }>;
      }>("/org/suppliers")
      .then((res) => {
        if (res.depots && res.distributors) {
          setSuppliers({ depots: res.depots, distributors: res.distributors });
        } else if (res.suppliers) {
          const d = res.suppliers
            .filter((s) => s.type === "depot")
            .map((s) => ({ id: s.id, name: s.name, code: s.code ?? "" }));
          const dist = res.suppliers
            .filter((s) => s.type === "distributor")
            .map((s) => ({ id: s.id, name: s.name }));
          setSuppliers({ depots: d, distributors: dist });
        }
      })
      .catch(async () => {
        try {
          const [depotsRes, distRes] = await Promise.all([
            api
              .get<
                | Array<{ id: string; name: string; code: string }>
                | { items: Array<{ id: string; name: string; code: string }> }
              >("/org/depots")
              .catch(() => []),
            api.get<Paginated<UserDto>>("/org/sales-reps?page=1&pageSize=100").catch(() => ({ items: [] })),
          ]);
          const dItems = Array.isArray(depotsRes) ? depotsRes : depotsRes.items || [];
          setSuppliers({
            depots: dItems,
            distributors: distRes.items.map((r) => ({ id: r.id, name: r.name })),
          });
        } catch {
          // ignore
        }
      });
  }, [lookups, seeTeam]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (q.trim()) query.set("q", q.trim());
      filters.apply(query);
      const result = await api.get<Paginated<Counter>>(`/org/counters?${query.toString()}`);
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load counters.");
    } finally {
      setLoading(false);
    }
  }, [page, q, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Apply a field change and clear only the errors for the fields it touched. */
  const patchForm = useCallback((patch: Partial<CreateForm>): void => {
    setForm((f) => ({ ...f, ...patch }));
    setFields((errs) => {
      const next = { ...errs };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  function counterPayload(): unknown {
    return {
      name: form.name,
      email: form.email,
      phone: form.phone,
      salesRepId: form.salesRepId || undefined,
      address: form.address || undefined,
      state: form.state || undefined,
      city: form.city || undefined,
      district: form.district || undefined,
      pincode: form.pincode || undefined,
      village: form.village || undefined,
      gstNumber: form.gstNumber || undefined,
      supplierType: form.supplierType,
      supplierId: form.supplierId || undefined,
      depotId: form.supplierType === "depot" ? form.supplierId || undefined : undefined,
    };
  }

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    const validation = validateForm(createCounterSchema, counterPayload());
    if (!validation.success) {
      setFields(validation.fields);
      return;
    }
    setFields({});
    setSaving(true);
    try {
      const result = await api.post<{ user: UserDto; tempPassword: string }>(
        "/org/counters",
        validation.data,
      );
      toast.success(`${result.user.name} added.`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setCreated({
        name: result.user.name,
        signInWith: result.user.email ?? result.user.phone ?? "",
        tempPassword: result.tempPassword,
      });
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Could not add this counter.");
      }
    } finally {
      setSaving(false);
    }
  }

  function openDetail(counter: Counter): void {
    setSelected(counter);
    setEditName(counter.name);
    setEditPhone(counter.phone ?? "");
    // A sales rep who has since been removed is not offered, so the field starts empty.
    const repGone = salesReps.length > 0 && !salesReps.some((r) => r.id === counter.salesRepId);
    setEditSalesRepId(counter.salesRepId && !repGone ? counter.salesRepId : "");
    setEditSupplierType((counter.supplierType as "org" | "depot" | "distributor") || "org");
    setEditSupplierId(counter.supplierId || counter.depotId || "");
    setEditAddress({
      state: counter.state ?? "",
      city: counter.city ?? "",
      district: counter.district ?? "",
      pincode: counter.pincode ?? "",
      village: counter.village ?? "",
      address: counter.address ?? "",
      gstNumber: counter.gstNumber ?? "",
    });
  }

  async function saveEdit(): Promise<void> {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const updated = await api.patch<Counter>(`/org/counters/${selected.id}`, {
        name: editName,
        // Older accounts may have no mobile yet; an empty field is left alone, not rejected.
        ...(editPhone.trim() ? { phone: editPhone } : {}),
        salesRepId: editSalesRepId || null,
        ...editAddress,
        supplierType: editSupplierType,
        supplierId: editSupplierId || null,
        depotId: editSupplierType === "depot" ? editSupplierId || null : null,
      });
      toast.success("Saved.");
      setSelected({ ...updated, karigarCount: selected.karigarCount });
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not save changes.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.del(`/org/counters/${toDelete.id}`);
      toast.success(`${toDelete.name} removed.`);
      setToDelete(null);
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not remove this counter.");
    } finally {
      setDeleting(false);
    }
  }

  function renderSupplierBadge(counter: Counter) {
    const type = counter.supplierType ?? "org";
    if (type === "depot") {
      const depot = suppliers.depots.find(
        (d) => d.id === counter.supplierId || d.id === counter.depotId,
      );
      return (
        <Badge tone="info">
          {depot ? `${depot.code} Direct` : "Depot Direct"}
        </Badge>
      );
    }
    if (type === "distributor" && counter.supplierId) {
      const dist = suppliers.distributors.find((d) => d.id === counter.supplierId);
      return <Badge tone="brand">{dist?.name ?? "Distributor"}</Badge>;
    }
    if (type === "sub_distributor") return <Badge tone="brand">Sub-distributor</Badge>;
    return <Badge tone="success">Factory Direct</Badge>;
  }

  const repOptions = salesReps.map((r) => ({ value: r.id, label: r.name }));

  return (
    <Page bleed>
      <PageHeader
        title="Counters"
      />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun="counter" /> : null}
              {canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add counter</Button> : null}
            </>
          }
        >
          <SearchInput
            label="Search counters"
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search all counter details…"
            value={q}
          />
          <ListFilters definitions={data?.filters?.filter((field) => field.key !== "salesRepId" || hasSalesRepTier)} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={6} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add counter</Button> : undefined}
              message={canCreate ? "Add a counter to generate its registration QR." : "No counters are in your network yet."}
              title="No counters yet"
            />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>Supplier</TH>
                {hasSalesRepTier ? <TH>Sales rep</TH> : null}
                <TH align="right">Karigars</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((counter) => (
                <TR key={counter.id} onClick={() => navigate(`${location.pathname}/${counter.id}`)}>
                  <TD>
                    <Link className="font-medium text-brand-ink hover:underline" to={`${location.pathname}/${counter.id}`} onClick={(event) => event.stopPropagation()}>{counter.name}</Link>{" "}
                    <Badge tone={statusTone(counter.status)}>{USER_STATUS_LABELS[counter.status]}</Badge>
                  </TD>
                  <TD className="font-mono text-xs">{counter.slug}</TD>
                  <TD>{renderSupplierBadge(counter)}</TD>
                  {hasSalesRepTier ? <TD>{counter.salesRepName ?? "—"}</TD> : null}
                  <TD align="right">{counter.karigarCount}</TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        { label: "View profile", onSelect: () => navigate(`${location.pathname}/${counter.id}`) },
                        { label: "Edit details", icon: Pencil, hidden: !canEdit, onSelect: () => openDetail(counter) },
                        { label: "Registration QR", icon: QrCode, onSelect: () => setQrCounter(counter) },
                        {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          hidden: !canDelete,
                          onSelect: () => setToDelete(counter),
                        },
                      ]}
                      label={counter.name}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>

      <SlideOver
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button form="create-counter" loading={saving} type="submit">
              Create
            </Button>
          </>
        }
        onClose={() => setCreateOpen(false)}
        open={createOpen}
        title="Add counter"
      >
        <form className="flex flex-col gap-5" id="create-counter" noValidate onSubmit={handleCreate}>
          <FormSection title="Counter">
          <TextInput
            autoComplete="name"
            error={fields.name}
            label="Counter name"
            onBlur={() => validateFieldOnBlur(createCounterSchema, counterPayload(), "name", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
              clearFieldError(setFields, "name");
            }}
            required
            value={form.name}
          />
          <TextInput
            autoComplete="email"
            error={fields.email}
            label="Email"
            onBlur={() => validateFieldOnBlur(createCounterSchema, counterPayload(), "email", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, email: e.target.value }));
              clearFieldError(setFields, "email");
            }}
            type="email"
            value={form.email}
          />
          <PhoneField
            error={fields.phone}
            hint="10-digit mobile number"
            label="Mobile"
            onBlur={() => validateFieldOnBlur(createCounterSchema, counterPayload(), "phone", setFields)}
            onChange={(digits) => {
              setForm((f) => ({ ...f, phone: digits }));
              clearFieldError(setFields, "phone");
            }}
            required
            value={form.phone}
          />
          </FormSection>

          {placeOnCreate ? (
          <FormSection description="Who this counter reports to, and where its stock comes from." title="Placement">
          {hasSalesRepTier ? (
            <SelectField
              error={fields.salesRepId}
              label="Sales rep"
              onBlur={() => validateFieldOnBlur(createCounterSchema, counterPayload(), "salesRepId", setFields)}
              onChange={(e) => {
                setForm((f) => ({ ...f, salesRepId: e.target.value }));
                clearFieldError(setFields, "salesRepId");
              }}
              options={repOptions}
              placeholder="None"
              value={form.salesRepId}
            />
          ) : null}
          <div className="flex flex-col gap-2 rounded-md border border-rule bg-surface p-3">
            <p className="font-title text-sm font-semibold text-ink">
              {t("सीधा सप्लायर (माल कहाँ से आता है?)", "Direct Supplier")}
            </p>
            <div className="flex flex-col gap-2 mt-1">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                <input
                  checked={form.supplierType === "org"}
                  className="accent-[var(--gq-color-1)]"
                  name="create-supplier-type"
                  onChange={() => setForm((f) => ({ ...f, supplierType: "org", supplierId: "" }))}
                  type="radio"
                />
                <span>Factory Direct ({payload.orgName ?? "Oliva Head Office"})</span>
              </label>

              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                <input
                  checked={form.supplierType === "depot"}
                  className="accent-[var(--gq-color-1)]"
                  name="create-supplier-type"
                  onChange={() => {
                    const defaultDepotId = suppliers.depots[0]?.id ?? "";
                    setForm((f) => ({ ...f, supplierType: "depot", supplierId: defaultDepotId }));
                  }}
                  type="radio"
                />
                <span>Depot Direct (Regional Warehouse)</span>
              </label>
              {form.supplierType === "depot" ? (
                <div className="ml-6">
                  <SelectField
                    label="Select Depot"
                    onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
                    options={suppliers.depots.map((d) => ({
                      value: d.id,
                      label: `${d.name} (${d.code})`,
                    }))}
                    placeholder={suppliers.depots.length === 0 ? "No depots available" : "Choose depot…"}
                    value={form.supplierId}
                  />
                </div>
              ) : null}

              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                <input
                  checked={form.supplierType === "distributor"}
                  className="accent-[var(--gq-color-1)]"
                  name="create-supplier-type"
                  onChange={() => {
                    const defaultDistId = suppliers.distributors[0]?.id ?? "";
                    setForm((f) => ({ ...f, supplierType: "distributor", supplierId: defaultDistId }));
                  }}
                  type="radio"
                />
                <span>Distributor (Standard Dealer Route)</span>
              </label>
              {form.supplierType === "distributor" ? (
                <div className="ml-6">
                  <SelectField
                    label="Select Distributor"
                    onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
                    options={suppliers.distributors.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                    placeholder={suppliers.distributors.length === 0 ? "No distributors available" : "Choose distributor…"}
                    value={form.supplierId}
                  />
                </div>
              ) : null}
            </div>
          </div>
          </FormSection>
          ) : null}

          <FormSection
            description="Optional, but it is what the dispatch paperwork prints. District and PIN fill themselves."
            title="Location"
          >
            <AddressFields errors={fields} onChange={patchForm} value={form} />
          </FormSection>
        </form>
      </SlideOver>

      {created ? (
        <TempPasswordModal
          name={created.name}
          signInWith={created.signInWith}
          onClose={() => setCreated(null)}
          open={Boolean(created)}
          tempPassword={created.tempPassword}
        />
      ) : null}

      <SlideOver
        footer={
          selected ? (
            <>
              {canDelete ? (
                <Button onClick={() => setToDelete(selected)} variant="danger">
                  Delete
                </Button>
              ) : null}
              <Button loading={savingEdit} onClick={() => void saveEdit()}>
                Save
              </Button>
            </>
          ) : null
        }
        onClose={() => setSelected(null)}
        open={Boolean(selected)}
        title={selected?.name ?? ""}
      >
        {selected ? (
          <div className="flex flex-col gap-5">
            <Badge tone={statusTone(selected.status)}>{USER_STATUS_LABELS[selected.status]}</Badge>
            <FormSection title="Counter">
              <TextInput label="Counter name" onChange={(e) => setEditName(e.target.value)} value={editName} />
              <TextInput label="Mobile" onChange={(e) => setEditPhone(e.target.value)} value={editPhone} />
            </FormSection>
            {placeOnEdit ? (
            <FormSection description="Who this counter reports to, and where its stock comes from." title="Placement">
            {hasSalesRepTier ? (
              <SelectField
                label="Sales rep"
                onChange={(e) => setEditSalesRepId(e.target.value)}
                options={repOptions}
                placeholder="None"
                value={editSalesRepId}
              />
            ) : null}
            <div className="flex flex-col gap-2 rounded-md border border-rule bg-surface p-3">
              <p className="font-title text-sm font-semibold text-ink">
                {t("सीधा सप्लायर (माल कहाँ से आता है?)", "Direct Supplier")}
              </p>
              <div className="flex flex-col gap-2 mt-1">
                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                  <input
                    checked={editSupplierType === "org"}
                    className="accent-[var(--gq-color-1)]"
                    name="edit-supplier-type"
                    onChange={() => {
                      setEditSupplierType("org");
                      setEditSupplierId("");
                    }}
                    type="radio"
                  />
                  <span>Factory Direct ({payload.orgName ?? "Oliva Head Office"})</span>
                </label>

                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                  <input
                    checked={editSupplierType === "depot"}
                    className="accent-[var(--gq-color-1)]"
                    name="edit-supplier-type"
                    onChange={() => {
                      setEditSupplierType("depot");
                      if (!editSupplierId && suppliers.depots[0]) {
                        setEditSupplierId(suppliers.depots[0].id);
                      }
                    }}
                    type="radio"
                  />
                  <span>Depot Direct (Regional Warehouse)</span>
                </label>
                {editSupplierType === "depot" ? (
                  <div className="ml-6">
                    <SelectField
                      label="Select Depot"
                      onChange={(e) => setEditSupplierId(e.target.value)}
                      options={suppliers.depots.map((d) => ({
                        value: d.id,
                        label: `${d.name} (${d.code})`,
                      }))}
                      placeholder={suppliers.depots.length === 0 ? "No depots available" : "Choose depot…"}
                      value={editSupplierId}
                    />
                  </div>
                ) : null}

                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                  <input
                    checked={editSupplierType === "distributor"}
                    className="accent-[var(--gq-color-1)]"
                    name="edit-supplier-type"
                    onChange={() => {
                      setEditSupplierType("distributor");
                      if (!editSupplierId && suppliers.distributors[0]) {
                        setEditSupplierId(suppliers.distributors[0].id);
                      }
                    }}
                    type="radio"
                  />
                  <span>Distributor (Standard Dealer Route)</span>
                </label>
                {editSupplierType === "distributor" ? (
                  <div className="ml-6">
                    <SelectField
                      label="Select Distributor"
                      onChange={(e) => setEditSupplierId(e.target.value)}
                      options={suppliers.distributors.map((d) => ({
                        value: d.id,
                        label: d.name,
                      }))}
                      placeholder={suppliers.distributors.length === 0 ? "No distributors available" : "Choose distributor…"}
                      value={editSupplierId}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            </FormSection>
            ) : null}

            <FormSection title="Location">
              <AddressFields
                errors={{}}
                onChange={(patch) => setEditAddress((a) => ({ ...a, ...patch }))}
                value={editAddress}
              />
            </FormSection>

            <div className="rounded-md border border-rule bg-surface p-3 text-sm text-muted">
              <p>Slug (immutable): {selected.slug}</p>
              <p>Email: {selected.email ?? "—"}</p>
              <p>Karigars: {selected.karigarCount}</p>
            </div>
          </div>
        ) : null}
      </SlideOver>

      <ConfirmDialog
        loading={deleting}
        message={`Remove ${toDelete?.name ?? "this counter"}? Transfer its karigars to another counter first if it has any.`}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        open={Boolean(toDelete)}
        title="Remove counter"
      />

      <Modal onClose={() => setQrCounter(null)} open={Boolean(qrCounter)} title={`Registration QR — ${qrCounter?.name ?? ""}`}>
        {qrCounter?.registrationUrl ? (
          <QrPosterPanel
            counterName={qrCounter.name}
            counterPhone={qrCounter.phone}
            qrPngUrl={`/api/org/counters/${qrCounter.id}/qr.png`}
            registrationUrl={qrCounter.registrationUrl}
          />
        ) : qrCounter ? (
          <Alert kind="danger">
            WhatsApp is not connected, so a registration QR cannot be made yet. Ask the platform admin to link WhatsApp.
          </Alert>
        ) : null}
      </Modal>
    </Page>
  );
}
